import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import CircleCheck from '@/assets/icons/circle-check.svg?react';
import CloseIcon from '@/assets/icons/close.svg?react';
import WarningTriangle from '@/assets/icons/warning-triangle.svg?react';
import WarningIcon from '@/assets/icons/warning.svg?react';

import APP_CONFIG from '@/_config/config.app';
import { cn } from '@/core/string.core';

const ToastProvider = ToastPrimitives.Provider;

// the viewport is the container that holds all the toasts
// and is responsible for positioning them
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 z-[100] flex max-h-screen w-full p-4 pb-14',
      'sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[460px]',
      'gap-2',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  cn(
    '',
    'pointer-events-auto relative flex w-full justify-between p-4 overflow-hidden',
    'bg-white rounded border',
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  ),
  {
    variants: {
      variant: {
        default: `border-system-success${APP_CONFIG.ACCESSIBILITY.ENABLED ? '-bg' : ''}`,
        error: 'border-system-error',
        warning: 'border-system-warning',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const toastDescriptionVariants = cva('flex flex-row items-center gap-4', {
  variants: {
    variant: {
      default: '',
      error: '',
      warning: '',
    },
  },
});

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root ref={ref} className={cn(toastVariants({ variant }), className)} {...props}>
      {props.children}
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'w-8 h-8 flex items-center justify-center',
      'focus:outline focus:outline-primary rounded',
      'hover:text-foreground focus:opacity-100',
      className,
    )}
    toast-close=""
    {...props}
  >
    <CloseIcon className="h-3 w-3 " />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} {...props}>
    <h2 className={cn('typo-primary-500', className)}>{props.children}</h2>
  </ToastPrimitives.Title>
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description> & VariantProps<typeof toastDescriptionVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn(toastDescriptionVariants({ variant }), className)} {...props}>
    <div className="grid grid-rows-1 grid-cols-[30px_1fr]">
      {variant === 'default' && (
        <div className="flex justify-center items-center mr-2">
          <CircleCheck className="min-w-6 min-h-6 text-system-success" />
        </div>
      )}

      {variant === 'error' && (
        <div className="flex justify-center items-center mr-2">
          <WarningTriangle className="min-w-6 min-h-6" />
        </div>
      )}

      {variant === 'warning' && (
        <div className="flex justify-center items-center mr-2">
          <WarningIcon className="min-w-6 min-h-6 text-system-warning" />
        </div>
      )}
      <p className="typo-primary-400">{props.children}</p>
    </div>
  </ToastPrimitives.Description>
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

export { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport, type ToastProps };
